/**
 * SvgImport.ts
 * -----------------------------------------------------------------------------
 * The inverse of DrawingExport.pageToSvg: a section drawing written as SVG,
 * read back into the `DrawingPage` the Canvas2DRenderer paints.
 *
 * WHY THIS EXISTS. Two drawings in the Property panel are authored as SVG in
 * C# rather than as a page of shapes:
 *
 *   - SectionSvgBuilder — the vendored components' dimensioned cross-sections,
 *     girder elevations and deck sections. The dimension lines are DERIVED by
 *     perturbing each parameter, so the layout lives in that builder and
 *     nowhere else.
 *   - PageSvgRenderer — an engine drawing page re-drawn in the same style.
 *
 * Inline SVG can only be zoomed with CSS: no pan, no cursor-anchored wheel, no
 * PNG / DXF export, and a fixed step ladder instead of continuous scale. Rather
 * than grow a second viewer for these, this parses the drawing into the same
 * model the canvas already renders, so a section gets the panel's real 2D
 * viewport for free.
 *
 * WHAT IT PARSES is deliberately not "SVG": it is the small dialect those two
 * builders emit — <path> with absolute M/L/Z, <line>, <polyline>, <polygon>,
 * <rect>, <circle>, <text> (positioned by x/y or by translate+rotate), grouped
 * in <g>, styled by the `sd-*` classes in modeler.css plus presentation
 * attributes and inline `style`. Anything else is skipped rather than guessed
 * at. Transforms other than the `translate(...) rotate(...)` on a dimension
 * label are NOT applied, because nothing emits them.
 *
 * ONE THING BESIDES SHAPES comes across: a `<g data-dim>` carries the
 * MEASUREMENT its rails and arrowheads picture, and that becomes a DimRecord on
 * the page. Without it the DXF these views export is loose lines and text where
 * the Drawing tab's is real DIMENSION entities — same writer, different page.
 * See dimOf, and SectionSvgBuilder.DimGroup which writes the attributes.
 *
 * COLOUR is resolved to the LIGHT theme's tokens, never to the live computed
 * value. Canvas2DRenderer inverts each shape colour's lightness in dark mode
 * (see its Css()), so handing it dark-theme ink would invert it back to black
 * on black. Authoring for the white sheet is the contract the rest of the
 * drawing pipeline already follows.
 */

import {
  DimRecord,
  DrawingPage,
  DrawShape,
  FillStyle,
  HatchStyle,
  Rgb,
  StrokeStyle,
} from './DrawingTypes';

// =============================================================
//  Palette — modeler.css tokens, light theme, flattened onto the white sheet
// =============================================================

/** A token colour at `alpha`, composited over the white drawing sheet — which
 *  is what `rgba(0,0,0,.65)` or `color-mix(… , transparent)` resolves to in the
 *  SVG copy. */
function over(c: Rgb, alpha: number): Rgb {
  return [
    1 - alpha * (1 - c[0]),
    1 - alpha * (1 - c[1]),
    1 - alpha * (1 - c[2]),
  ];
}

const BLACK: Rgb = [0, 0, 0];
/** --accent, light theme (#6A89A7). */
const ACCENT: Rgb = [0.416, 0.537, 0.655];
/** --dim-line, light theme (#6A89A7): dimension lines, extensions, arrowheads. */
const DIM_LINE: Rgb = [0.416, 0.537, 0.655];
/** --dim-text, light theme (#444444): the callout text on those lines. */
const DIM_TEXT: Rgb = [0.267, 0.267, 0.267];
/** --text-primary / secondary / tertiary, light theme. */
const PRIMARY: Rgb = over(BLACK, 0.9);
const SECONDARY: Rgb = over(BLACK, 0.8);
const TERTIARY: Rgb = over(BLACK, 0.65);
/** --canvas2d-bg, light theme: the sheet a dimension label breaks the line on. */
const SHEET: Rgb = [1, 1, 1];

// =============================================================
//  The sd-* stylesheet, as data
// =============================================================

/** What a class contributes. Later classes on the same element win, and
 *  presentation attributes / inline style win over all of them — the cascade
 *  the SVG copy gets from modeler.css. */
interface ClassStyle {
  stroke?: Rgb;
  fill?: Rgb | null;
  /** Outline weight in SCREEN pixels, so the profile stays heavier than the
   *  dimension rails at every zoom (the canvas otherwise draws every line at
   *  one pixel). */
  width?: number;
  dash?: number[];
  /** Text size in world units — overridden by an inline font-size. */
  textSize?: number;
  /** Fill the interior with hatch rather than colour. */
  hatched?: boolean;
  /** Monospaced text, the way the stylesheet sets dimension values. */
  mono?: boolean;
}

/**
 * Mirrors the `.mdl-svgdwg .sd-*` block in modeler.css. Kept as a table rather
 * than read from the live stylesheet because the drawing has to be resolvable
 * before it is in the document, and because the live values are theme-dependent
 * (see the note on colour at the top).
 */
const CLASS_STYLE: Record<string, ClassStyle> = {
  // ── concrete ──────────────────────────────────────────────────────────────
  // .sd-fill carries opacity .5 in CSS; here that lands on the hatch colour.
  'sd-fill': { hatched: true, fill: null, stroke: undefined },
  'sd-edge': { stroke: PRIMARY, fill: null, width: 2 },
  'sd-cl': { stroke: TERTIARY, width: 1, dash: [15, 3.5, 2, 3.5] },

  // ── dimensions ────────────────────────────────────────────────────────────
  'sd-ln': { stroke: DIM_LINE, width: 1.1 },
  'sd-ext': { stroke: over(DIM_LINE, 0.6), width: 0.8 },
  'sd-arw': { fill: DIM_LINE, stroke: undefined },
  'sd-tx': { fill: DIM_TEXT, textSize: 13, mono: true },
  'sd-unit': { fill: TERTIARY, textSize: 10 },

  // ── girder elevation: where the SectionKind changes ───────────────────────
  'sd-zone': { stroke: over(DIM_LINE, 0.65), width: 0.9, dash: [7, 4] },
  'sd-z-tr': { dash: [3, 3] },

  // ── deck section layers ───────────────────────────────────────────────────
  'sd-layer': { stroke: PRIMARY, fill: over(TERTIARY, 0.22), width: 1.6 },
  'sd-l-slab': { fill: over(TERTIARY, 0.14) },
  'sd-l-girder': { fill: over(ACCENT, 0.2) },
  'sd-l-haunch': { fill: over(ACCENT, 0.12), width: 1.1 },
  'sd-l-wearingsurface': { fill: over(TERTIARY, 0.3), stroke: SECONDARY, width: 1 },
  'sd-l-barrier': { fill: over(TERTIARY, 0.26), width: 1.3 },
  'sd-l-railing': { fill: over(TERTIARY, 0.26), width: 1.3 },
  'sd-l-divider': { fill: over(TERTIARY, 0.26), width: 1.3 },
  'sd-l-walkway': { fill: over(TERTIARY, 0.18), width: 1.3 },
};

/** Hatch ink: --text-tertiary through .sd-fill's opacity .5. */
const HATCH_COLOR: Rgb = over(TERTIARY, 0.5);

/** Fallback hatch when a fill names a pattern that isn't in <defs> — the
 *  spacing and 45° both builders use. */
const DEFAULT_HATCH: HatchStyle = { spacing: 7, angle: 45, color: HATCH_COLOR };

// =============================================================
//  Entry point
// =============================================================

/**
 * Parse a section drawing into a page the 2D canvas can render, or null when
 * the markup is not one of ours / has no viewBox / yields nothing to draw. A
 * null is a signal to fall back to showing the SVG inline, not an error to
 * report: a drawing is worth more than a message about a drawing.
 */
export function svgToPage(svg: string): DrawingPage | null {
  if (!svg || typeof DOMParser === 'undefined') return null;

  let root: SVGSVGElement | null = null;
  try {
    const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
    if (doc.getElementsByTagName('parsererror').length > 0) return null;
    root = doc.documentElement as unknown as SVGSVGElement;
  } catch {
    return null;
  }
  if (!root || root.nodeName.toLowerCase() !== 'svg') return null;

  const box = numbers(root.getAttribute('viewBox') ?? '');
  if (box.length !== 4 || !(box[2] > 0) || !(box[3] > 0)) return null;
  const [minX, minY, vw, vh] = box;

  // Page coordinates start at (0,0) — FitToWindow fits 0..paperWidth — so the
  // viewBox origin is folded into every coordinate here rather than carried.
  const ox = -minX;
  const oy = -minY;

  const hatches = collectPatterns(root);
  const shapes: DrawShape[] = [];
  const dims: DimRecord[] = [];
  walk(root, shapes, ox, oy, hatches, dims);
  if (shapes.length === 0) return null;

  return dims.length > 0
    ? { paperWidth: vw, paperHeight: vh, shapes, dims }
    : { paperWidth: vw, paperHeight: vh, shapes };
}

// =============================================================
//  Traversal
// =============================================================

/** Elements whose subtree describes how to paint, not what to paint. */
const SKIP = new Set(['defs', 'title', 'desc', 'metadata', 'style', 'clippath', 'mask']);

function walk(
  el: Element,
  out: DrawShape[],
  ox: number,
  oy: number,
  hatches: Map<string, HatchStyle>,
  dims: DimRecord[],
): void {
  for (let i = 0; i < el.children.length; i++) {
    const child = el.children[i];
    const tag = child.nodeName.toLowerCase();
    if (SKIP.has(tag)) continue;
    if (tag === 'g' || tag === 'svg') {
      // A group that carries a measurement (SectionSvgBuilder.DimGroup) is a
      // dimension: everything drafted inside it is the picture of one, and the
      // shape range it spans is what pairs the two. Recorded AFTER the walk so
      // the range is the one the shapes actually landed in.
      const from = out.length;
      walk(child, out, ox, oy, hatches, dims);
      const d = dimOf(child, from, out.length, ox, oy);
      if (d) dims.push(d);
      continue;
    }
    emit(child, tag, out, ox, oy, hatches);
    // A shape with children is not something either builder emits, but
    // descending costs nothing and keeps an unexpected wrapper from hiding
    // half a drawing.
    if (child.children.length > 0) walk(child, out, ox, oy, hatches, dims);
  }
}

/**
 * The measurement a `<g data-dim>` carries, as a DimRecord over the shapes it
 * just drafted — or null for any other group.
 *
 * Only `pageToDxf` reads these, and only to write a real DIMENSION entity in
 * front of the block that pictures it; a group whose attributes are malformed
 * simply exports as the loose lines it always did. See DrawingTypes.DimRecord
 * and SectionSvgBuilder.DimGroup, which is the other half of this.
 */
function dimOf(
  el: Element,
  from: number,
  to: number,
  ox: number,
  oy: number,
): DimRecord | null {
  const raw = el.getAttribute('data-dim');
  if (!raw || to <= from) return null;

  const v = numbers(raw);
  if (v.length !== 6) return null;

  const text = (el.getAttribute('data-dim-text') ?? '').trim();
  if (!text) return null;

  return {
    from,
    to,
    x1: v[0] + ox,
    y1: v[1] + oy,
    x2: v[2] + ox,
    y2: v[3] + oy,
    lineX: v[4] + ox,
    lineY: v[5] + oy,
    text,
    vertical: el.getAttribute('data-dim-v') === '1',
  };
}

function emit(
  el: Element,
  tag: string,
  out: DrawShape[],
  ox: number,
  oy: number,
  hatches: Map<string, HatchStyle>,
): void {
  const st = resolve(el, hatches);

  switch (tag) {
    case 'line': {
      const x1 = num(el, 'x1') + ox;
      const y1 = num(el, 'y1') + oy;
      const x2 = num(el, 'x2') + ox;
      const y2 = num(el, 'y2') + oy;
      out.push({ kind: 'line', x1, y1, x2, y2, style: strokeOf(st) });
      break;
    }

    case 'polyline': {
      const pts = pointList(el.getAttribute('points'), ox, oy);
      if (pts.length >= 2) out.push({ kind: 'polyline', points: pts, style: strokeOf(st) });
      break;
    }

    case 'polygon': {
      const pts = pointList(el.getAttribute('points'), ox, oy);
      if (pts.length >= 3) out.push({ kind: 'polygon', points: pts, style: fillOf(st) });
      break;
    }

    case 'rect': {
      const w = num(el, 'width');
      const h = num(el, 'height');
      if (w > 0 && h > 0) {
        out.push({
          kind: 'rect',
          x: num(el, 'x') + ox,
          y: num(el, 'y') + oy,
          w,
          h,
          style: fillOf(st),
        });
      }
      break;
    }

    case 'circle': {
      const r = num(el, 'r');
      if (r > 0) {
        out.push({
          kind: 'circle',
          cx: num(el, 'cx') + ox,
          cy: num(el, 'cy') + oy,
          r,
          style: fillOf(st),
        });
      }
      break;
    }

    case 'path':
      emitPath(el, st, out, ox, oy);
      break;

    case 'text':
      emitText(el, st, out, ox, oy);
      break;
  }
}

/**
 * A path is one or more closed M…L…Z rings — a section profile with its voids,
 * or one ring per deck layer.
 *
 * FILLED paths become a single polygon whose extra rings are holes, so the
 * even-odd rule that makes a box girder's cells read as voids survives. UNFILLED
 * paths (the profile outline drawn over the fill) become one polyline per ring,
 * closed by repeating the first point: a polyline carries StrokeStyle, which is
 * what gives the profile its constant screen weight.
 */
function emitPath(el: Element, st: Resolved, out: DrawShape[], ox: number, oy: number): void {
  const rings = parsePathRings(el.getAttribute('d') ?? '', ox, oy);
  if (rings.length === 0) return;

  const filled = st.fill != null || st.hatch != null;
  if (filled) {
    const [first, ...holes] = rings.filter((r) => r.length >= 3);
    if (!first) return;
    out.push({ kind: 'polygon', points: first, holes, style: fillOf(st) });
    return;
  }

  const stroke = strokeOf(st);
  for (const ring of rings) {
    if (ring.length < 2) continue;
    const closed =
      ring.length > 2 &&
      (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]);
    out.push({
      kind: 'polyline',
      points: closed ? [...ring, ring[0]] : ring,
      style: stroke,
    });
  }
}

/**
 * Text is positioned either by x/y (+ an optional dy nudge) or by
 * `translate(x,y) rotate(a)` — the vertical dimension labels.
 *
 * SVG puts the baseline on y, which is `valign: 'bottom'` in the page model.
 * SVG angles are clockwise (page y runs down) while TextStyle.rotate is
 * counter-clockwise, hence the sign flip: the builders' rotate(-90) is a label
 * reading bottom-to-top, which is rotate: 90 here.
 */
function emitText(el: Element, st: Resolved, out: DrawShape[], ox: number, oy: number): void {
  const text = (el.textContent ?? '').trim();
  if (!text) return;

  const size = st.textSize ?? 10;
  const anchor = (el.getAttribute('text-anchor') ?? 'start') as 'start' | 'middle' | 'end';
  const dy = num(el, 'dy');

  const tf = parseTranslateRotate(el.getAttribute('transform'));
  const x = (tf ? tf.x : num(el, 'x')) + ox;
  const y = (tf ? tf.y : num(el, 'y')) + oy + dy;
  const rotate = tf ? -tf.angle : 0;

  out.push({
    kind: 'text',
    x,
    y,
    text,
    style: {
      color: st.fill ?? st.stroke ?? PRIMARY,
      size,
      anchor: anchor === 'middle' || anchor === 'end' ? anchor : 'start',
      // A rotated label is centred on its anchor by the renderer, which is what
      // the break in the rule around it is sized for; an upright one sits on
      // its baseline exactly where the builder put it.
      valign: rotate ? 'middle' : 'bottom',
      rotate,
      mono: st.mono,
    },
  });
}

// =============================================================
//  Style resolution
// =============================================================

interface Resolved {
  stroke?: Rgb;
  fill?: Rgb | null;
  width?: number;
  dash?: number[];
  textSize?: number;
  mono?: boolean;
  hatch?: HatchStyle;
}

function resolve(el: Element, hatches: Map<string, HatchStyle>): Resolved {
  const r: Resolved = {};

  // 1. Classes, in source order — the stylesheet.
  const classes = (el.getAttribute('class') ?? '').split(/\s+/);
  for (const c of classes) {
    const cs = CLASS_STYLE[c];
    if (!cs) continue;
    if (cs.stroke !== undefined) r.stroke = cs.stroke;
    if (cs.fill !== undefined) r.fill = cs.fill;
    if (cs.width !== undefined) r.width = cs.width;
    if (cs.dash !== undefined) r.dash = cs.dash;
    if (cs.textSize !== undefined) r.textSize = cs.textSize;
    if (cs.mono !== undefined) r.mono = cs.mono;
    if (cs.hatched) r.hatch = DEFAULT_HATCH;
  }

  // 2. Presentation attributes and inline style — both builders set the
  //    scale-dependent parts (stroke width, font size) inline, and paint a
  //    hatch by naming its pattern.
  const inline = styleMap(el.getAttribute('style'));
  const fill = inline.get('fill') ?? el.getAttribute('fill');
  if (fill) {
    const ref = patternRef(fill);
    if (ref) {
      r.hatch = hatches.get(ref) ?? DEFAULT_HATCH;
      r.fill = null;
    } else if (fill === 'none') {
      r.fill = null;
      r.hatch = undefined;
    } else {
      const c = parseColor(fill);
      if (c) r.fill = c;
    }
  }
  const stroke = inline.get('stroke') ?? el.getAttribute('stroke');
  if (stroke) {
    if (stroke === 'none') r.stroke = undefined;
    else {
      const c = parseColor(stroke);
      if (c) r.stroke = c;
    }
  }
  const sw = inline.get('stroke-width') ?? el.getAttribute('stroke-width');
  if (sw) {
    const v = parseFloat(sw);
    if (isFinite(v) && v > 0) r.width = v;
  }
  const da = inline.get('stroke-dasharray') ?? el.getAttribute('stroke-dasharray');
  if (da) {
    const d = numbers(da);
    r.dash = d.length > 0 ? d : undefined;
  }
  const fs = inline.get('font-size') ?? el.getAttribute('font-size');
  if (fs) {
    const v = parseFloat(fs);
    if (isFinite(v) && v > 0) r.textSize = v;
  }

  return r;
}

function strokeOf(st: Resolved): StrokeStyle {
  return {
    color: st.stroke ?? PRIMARY,
    width: st.width,
    screenWidth: st.width,
    dash: st.dash,
  };
}

function fillOf(st: Resolved): FillStyle {
  return {
    fill: st.fill ?? null,
    stroke: st.stroke ?? null,
    width: st.width,
    screenWidth: st.width,
    dash: st.dash,
    hatch: st.hatch,
  };
}

/**
 * The <defs> hatch patterns, by id. Both builders emit the same shape of
 * pattern — a `patternUnits="userSpaceOnUse"` tile of one line, rotated by
 * `patternTransform` — so the tile width IS the line spacing.
 */
function collectPatterns(root: Element): Map<string, HatchStyle> {
  const map = new Map<string, HatchStyle>();
  const pats = root.getElementsByTagName('pattern');
  for (let i = 0; i < pats.length; i++) {
    const p = pats[i];
    const id = p.getAttribute('id');
    if (!id) continue;
    const spacing = parseFloat(p.getAttribute('width') ?? '');
    const m = /rotate\(\s*(-?[\d.]+)/.exec(p.getAttribute('patternTransform') ?? '');
    map.set(id, {
      spacing: isFinite(spacing) && spacing > 0 ? spacing : DEFAULT_HATCH.spacing,
      angle: m ? parseFloat(m[1]) : DEFAULT_HATCH.angle,
      color: HATCH_COLOR,
    });
  }
  return map;
}

// =============================================================
//  Small parsers
// =============================================================

function num(el: Element, name: string): number {
  const v = parseFloat(el.getAttribute(name) ?? '');
  return isFinite(v) ? v : 0;
}

/** Every number in a string, in order — viewBox, dasharray, points. */
function numbers(s: string): number[] {
  const out: number[] = [];
  const re = /-?\d*\.?\d+(?:[eE][-+]?\d+)?/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    const v = parseFloat(m[0]);
    if (isFinite(v)) out.push(v);
  }
  return out;
}

function pointList(
  s: string | null,
  ox: number,
  oy: number,
): Array<[number, number]> {
  const n = numbers(s ?? '');
  const out: Array<[number, number]> = [];
  for (let i = 0; i + 1 < n.length; i += 2) out.push([n[i] + ox, n[i + 1] + oy]);
  return out;
}

/**
 * `d` as a list of rings. Only absolute M / L / Z appear in these drawings —
 * anything else ends the current ring rather than being mis-drawn as a line.
 */
function parsePathRings(d: string, ox: number, oy: number): Array<Array<[number, number]>> {
  const rings: Array<Array<[number, number]>> = [];
  let ring: Array<[number, number]> = [];
  const re = /([A-Za-z])([^A-Za-z]*)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d)) !== null) {
    const cmd = m[1];
    const args = numbers(m[2]);
    switch (cmd) {
      case 'M':
        if (ring.length >= 2) rings.push(ring);
        ring = [];
        for (let i = 0; i + 1 < args.length; i += 2) ring.push([args[i] + ox, args[i + 1] + oy]);
        break;
      case 'L':
        for (let i = 0; i + 1 < args.length; i += 2) ring.push([args[i] + ox, args[i + 1] + oy]);
        break;
      case 'Z':
      case 'z':
        if (ring.length >= 2) rings.push(ring);
        ring = [];
        break;
      default:
        // A curve or a relative command: stop the ring here rather than
        // inventing a straight line through control points.
        if (ring.length >= 2) rings.push(ring);
        ring = [];
        break;
    }
  }
  if (ring.length >= 2) rings.push(ring);
  return rings;
}

/** `translate(x,y) rotate(a)` — the only transform these drawings use. */
function parseTranslateRotate(
  tf: string | null,
): { x: number; y: number; angle: number } | null {
  if (!tf) return null;
  const t = /translate\(([^)]*)\)/.exec(tf);
  if (!t) return null;
  const xy = numbers(t[1]);
  const r = /rotate\(([^)]*)\)/.exec(tf);
  const a = r ? numbers(r[1]) : [];
  return { x: xy[0] ?? 0, y: xy[1] ?? 0, angle: a[0] ?? 0 };
}

/** `style="a:b;c:d"` as a lookup. */
function styleMap(style: string | null): Map<string, string> {
  const map = new Map<string, string>();
  if (!style) return map;
  for (const part of style.split(';')) {
    const i = part.indexOf(':');
    if (i <= 0) continue;
    map.set(part.slice(0, i).trim().toLowerCase(), part.slice(i + 1).trim());
  }
  return map;
}

/** The id inside `url(#id)`, or null. */
function patternRef(v: string): string | null {
  const m = /^url\(\s*#([^)\s]+)\s*\)$/.exec(v.trim());
  return m ? m[1] : null;
}

/**
 * `#rgb`, `#rrggbb`, `rgb(...)` and `rgba(...)` — the forms a colour can take
 * once it has come through a token. A CSS variable or a named colour returns
 * null, which leaves the class's own colour in place.
 */
function parseColor(v: string): Rgb | null {
  const s = v.trim().toLowerCase();
  if (s.startsWith('#')) {
    const hex = s.slice(1);
    if (hex.length === 3) {
      return [
        parseInt(hex[0] + hex[0], 16) / 255,
        parseInt(hex[1] + hex[1], 16) / 255,
        parseInt(hex[2] + hex[2], 16) / 255,
      ];
    }
    if (hex.length === 6) {
      return [
        parseInt(hex.slice(0, 2), 16) / 255,
        parseInt(hex.slice(2, 4), 16) / 255,
        parseInt(hex.slice(4, 6), 16) / 255,
      ];
    }
    return null;
  }
  if (s.startsWith('rgb')) {
    const n = numbers(s);
    if (n.length >= 3) {
      const c: Rgb = [n[0] / 255, n[1] / 255, n[2] / 255];
      // An alpha resolves against the white sheet, the same as the tokens above.
      return n.length >= 4 && n[3] <= 1 ? over(c, n[3]) : c;
    }
    return null;
  }
  if (s === 'black') return BLACK;
  if (s === 'white') return SHEET;
  return null;
}
